import { State } from '@progress/kendo-data-query';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { FC, useCallback, useMemo, useState } from 'react';
import ExportExcel, { defaultExcelOption } from 'src/components/ExportExcel';
import ClearFilterButton from 'src/components/design-system/ClearFilterButton';
import useCompany from 'src/hooks/useCompany';
import useVarianceApi from 'src/services/useVarianceApi';
import { ViewVariance } from 'src/types/ViewVariance';
import { SetState } from 'src/types/generic';
import { modifyRequest } from 'src/utils/requestInterceptor';
import { formatVariances } from 'src/utils/transformation';

interface GridToolbarProps {
  clearFilter: () => void;
  excelExport: (data: any[]) => void;
  rows: ViewVariance[];
  varianceDataState: State;
  dateField: string;
  setExportLoading: SetState<boolean>;
  isClearFiltersDisabled: boolean;
}

const VarianceToolBar: FC<GridToolbarProps> = ({
  clearFilter,
  excelExport,
  rows,
  varianceDataState,
  dateField,
  setExportLoading,
  isClearFiltersDisabled,
}) => {
  const [excelOption, setExcelOption] = useState(defaultExcelOption);
  const { company } = useCompany();
  const { getVarianceList } = useVarianceApi();
  const data = useMemo(() => {
    const filters = modifyRequest(varianceDataState, [dateField]);
    return {
      clientSide: true,
      ...filters,
    };
  }, [dateField, varianceDataState]);

  const listCompanyReceipts = useCallback(
    () => getVarianceList({ companyId: company.companyId, body: data }),
    [company.companyId, data, getVarianceList]
  );

  const handleExcelChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const selectedOption = e.target.value;
      setExcelOption(selectedOption);
      if (selectedOption.value === 1) {
        setExportLoading(true);
        excelExport(formatVariances(rows));
        setExcelOption(defaultExcelOption);
        setExportLoading(false);
      } else if (selectedOption.value === 2) {
        setExportLoading(true);
        listCompanyReceipts()
          .then((res) => {
            excelExport(formatVariances(res?.items || []));
            setExcelOption(defaultExcelOption);
            setExportLoading(false);
          })
          .catch((err) => {
            console.error(err);
            setExportLoading(false);
          });
      }
    },
    [excelExport, listCompanyReceipts, rows, setExportLoading]
  );

  return (
    <div className="flex items-center justify-between w-full">
      <ClearFilterButton onClick={clearFilter} disabled={isClearFiltersDisabled} />
      <div>
        <ExportExcel excelOption={excelOption} onExcelChanged={handleExcelChange} width={200} />
      </div>
    </div>
  );
};
export default VarianceToolBar;
